import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import PlanCard from "@/components/PlanCard";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import api from "@/lib/api";
import { fetchPlans, FALLBACK_PLANS } from "@/lib/plans";
import useScrollReveal from "@/hooks/useScrollReveal";
import {
  ShieldCheck, Stethoscope, Heart, Phone, Star, ArrowRight, Sparkles,
  Activity, Baby, Brain, Bone, Eye, Ear, FlaskConical, Ambulance,
  HeartPulse, Pill, Award, Clock3, Users, MapPin, BadgeCheck, CheckCircle2, ScanHeart, Cross
} from "lucide-react";
import herohomeImage1 from "@/assets/npc-home-hero-1.jpeg";
import herohomeImage2 from "@/assets/npc-home-hero-2.jpg";
import herohomeImage3 from "@/assets/npc-home-hero-3.jpg";
import herohomeImage4 from "@/assets/npc-home-hero-4.jpg";
import herohomeImage5 from "@/assets/npc-home-hero-5.jpg";

/* Hero slider — local hospital image + premium medical stock images */
const HERO_SLIDES = [
  { src: herohomeImage1, alt: "Nirmal Multi Speciality Hospital — Surat" },
  { src: herohomeImage2, alt: "Modern hospital lobby" },
  { src: herohomeImage3, alt: "Specialist doctor in consult" },
  { src: herohomeImage4, alt: "Advanced diagnostic care" },
  { src: herohomeImage5, alt: "Patient in recovery" },
];

const IMG = {
  doctor: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1100&q=80",
  family: "https://images.unsplash.com/photo-1535572290543-960a8046f5af?auto=format&fit=crop&w=1100&q=80",
  clinic: "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=1100&q=80",
};

const SPECIALTIES = [
  { icon: HeartPulse, name: "Cardiology" },
  { icon: Brain, name: "Neurology" },
  { icon: Baby, name: "Pediatrics" },
  { icon: Bone, name: "Orthopedics" },
  { icon: Activity, name: "Oncology" },
  { icon: ScanHeart, name: "General Surgery" },
  { icon: Cross, name: "Neuro Surgery" },
  { icon: Eye, name: "Ophthalmology" },
  { icon: Ear, name: "ENT" },
  { icon: FlaskConical, name: "Pathology" },
  { icon: Stethoscope, name: "General Medicine" },
  { icon: Pill, name: "Pharmacy" },
];

const DOCTORS = [
  { n: "Dr. Naresh Mehta", s: "Cardiology", e: "20+ yrs", color: "1E4F87" },
  { n: "Dr. Shivangi Choraria", s: "Pediatrics", e: "15+ yrs", color: "A53A2A" },
  { n: "Dr. Bhushan Dodia", s: "Orthopedics", e: "18+ yrs", color: "0F2540" },
  { n: "Dr. Jagruti Desai", s: "Gynecology", e: "22+ yrs", color: "C89B3C" },
];
const docAvatar = (d) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(d.n.replace("Dr. ", ""))}&background=${d.color}&color=FFFFFF&bold=true&size=400&font-size=0.36`;

export default function Home() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  useScrollReveal();
  useEffect(() => { let ok = true; fetchPlans().then((p) => { if (ok) setPlans(p); }); return () => { ok = false; }; }, []);

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="hero-grad relative overflow-hidden" data-testid="hero-section">
        {/* Decorative dotted backdrop */}
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <div className="fade-up inline-flex items-center gap-3 group">
              <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#A53A2A]" />
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#1F7A3D] opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1F7A3D]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[#0F2540]">
                Now open · <span className="text-[#A53A2A]">Adajan, Surat</span>
              </span>
              <span className="h-px w-10 bg-gradient-to-l from-transparent to-[#A53A2A]" />
            </div>
            <h1 className="mt-5 font-display text-7xl sm:text-7xl lg:text-[5.75rem] xl:text-[5.75rem] font-bold text-[#0F2540] leading-[1.08] tracking-tight fade-up delay-1">
              A hospital <br/>with a <span className="bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#A53A2A] bg-clip-text text-transparent">difference.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#5C6B7A] leading-relaxed fade-up delay-2">
              At Nirmal Super Speciality Hospital, healthcare goes beyond infrastructure and technology — it is built on compassion, trust, transparency, and timely medical care.
Our goal is to create long-term healthcare relationships that support individuals and families at every stage of life.
 
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-up delay-3">
              <Link to="/join" className="btn-primary text-base px-7 py-4" data-testid="hero-view-plans-btn">
                Join NPC Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/plans" className="btn-ghost text-base px-7 py-4" data-testid="hero-contact-btn">View Plans</Link>
            </div>
            {/* <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#5C6B7A] fade-up delay-4">
              <div className="flex items-center gap-2"><BadgeCheck className="h-5 w-5 text-[#1E4F87]" /> NABH-aligned protocols</div>
              <div className="flex items-center gap-2"><Award className="h-5 w-5 text-[#A53A2A]" /> Trusted by 1.2L+ families</div>
              <div className="flex items-center gap-2"><Clock3 className="h-5 w-5 text-[#1E4F87]" /> Quality care you trust</div>
            </div> */}
          </div>

          <div className="lg:col-span-5 relative">
            {/* Hero slider — auto-rotating with crossfade + dot indicators */}
            <HeroSlider />
          </div>

            {/* Floating: member ID badge */}
            {/* <div className="absolute -bottom-6 -left-6 lux-card rounded-2xl p-4 w-64 float">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1E4F87]">
                <Sparkles className="h-4 w-4 text-[#A53A2A]" /> NPC001001
              </div>
              <p className="mt-1 text-xs text-[#5C6B7A]">Mr. R. Sharma · Family Premium</p>
              <p className="mt-2 text-xs text-[#1F7A3D] flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Activated · Valid till 2027</p>
            </div> */}

            {/* Floating: rating */}
            {/* <div className="absolute top-1/2 -right-3 lux-card rounded-2xl px-4 py-3 hidden md:flex items-center gap-3 float" style={{ animationDelay: "2s" }}>
              <div className="flex">
                {[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-[#C89B3C] text-[#C89B3C]" />)}
              </div>
              <div>
                <div className="text-xs font-bold text-[#0F2540]">4.9 / 5</div>
                <div className="text-[10px] text-[#5C6B7A]">2,400+ reviews</div>
              </div>
            </div> */}
        </div>
      </section>

      {/* MARQUEE SPECIALTIES */}
      <section className="border-y border-[#E1E8F0] bg-white/60 overflow-hidden" style={{ isolation: "isolate" }}>
        <div className="py-6">
          <div className="marquee text-[#1E4F87]">
            {[...SPECIALTIES, ...SPECIALTIES].map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-base font-medium whitespace-nowrap">
                <s.icon className="h-5 w-5 text-[#A53A2A]" /> {s.name}
                <span className="opacity-30 ml-4">·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          [Users, "30+", "Specialist doctors", "across 20 specialties"],
          [Activity, "1.2L+", "Patients served", "Adajan & west Surat"],
          [Ambulance, "24×7", "Ambulance", "emergency response"],
          [Award, "₹2,000", "Membership from", "/year for 1 member"],
        ].map(([Icon, n, l, sub], i) => (
          <div key={l} className="reveal lux-card rounded-2xl p-7 text-left" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="h-11 w-11 rounded-xl bg-[#EAF1FA] text-[#1E4F87] grid place-items-center"><Icon className="h-5 w-5" /></div>
            <div className="mt-5 font-display text-4xl lg:text-5xl font-bold text-[#1E4F87]">{n}</div>
            <div className="text-sm font-semibold text-[#0F2540] mt-1">{l}</div>
            <div className="text-xs text-[#5C6B7A] mt-1">{sub}</div>
          </div>
        ))}
      </section> */}

      {/* HEALTHCARE ECOSYSTEM — pillar cards */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-6" data-testid="home-ecosystem-section">
        <div className="max-w-3xl reveal">
          <span className="overline">Your family's</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540] leading-tight">
            Healthcare <span className="text-[#A53A2A]">Ecosystem.</span>
          </h2>
          <p className="mt-4 text-[#5C6B7A] text-lg leading-relaxed">
            Nirmal Privilege Card is more than a discount card — it is a comprehensive healthcare support ecosystem designed to keep your family connected with trusted medical care throughout the year.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Stethoscope, t: "Specialist OPD", d: "Unlimited consultations with eligible doctors during regular OPD hours." },
            { icon: FlaskConical, t: "Diagnostics",    d: "Privileged rates on pathology, radiology and preventive health packages." },
            { icon: Pill,         t: "Pharmacy",       d: "In-house pharmacy benefits across eligible medications." },
            { icon: BadgeCheck,   t: "Hospital Billing", d: "Privileged rates on eligible hospital services and procedures." },
            { icon: HeartPulse,   t: "Preventive Care", d: "Guidance, follow-ups and continuous health support all year round." },
            { icon: Users,        t: "Wellness Initiatives", d: "Awareness programs and family-focused wellness camps." },
          ].map((b, i) => (
            <div key={b.t} className="reveal group relative rounded-2xl bg-white p-7 ring-1 ring-[#E1E8F0] hover:ring-[#C89B3C]/45 hover:-translate-y-1 hover:shadow-[0_24px_50px_-25px_rgba(15,37,64,0.25)] transition-all duration-500 overflow-hidden" style={{ animationDelay: `${i * 70}ms` }} data-testid={`ecosystem-card-${i}`}>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#A53A2A] via-[#C89B3C] to-[#1E4F87] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#A53A2A] to-[#7A2A1E] grid place-items-center text-white shadow-[0_10px_30px_-10px_rgba(165,58,42,0.55)] group-hover:scale-110 transition-transform duration-500">
                <b.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-[#0F2540]">{b.t}</h3>
              <p className="mt-2 text-sm text-[#5C6B7A] leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="max-w-2xl reveal">
          <span className="overline">Membership benefits</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">Everything your family needs to stay well.</h2>
          <p className="mt-4 text-[#5C6B7A] text-lg">Designed around real Indian family needs — from elderly parents to growing children.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Stethoscope, t: "Unlimited OPD consultations", d: "Consult eligible specialists multiple times during OPD hours — General Physician, Orthopedics, Gynecology, Pediatrics & General Surgery." },
            { icon: Heart, t: "Preventive healthcare support", d: "Special rates on diagnostics, pathology, radiology and preventive health-check packages — performed within hospital infrastructure." },
            { icon: Users, t: "Family coverage", d: "Couple, children and parents on a single membership. Add or change covered members anytime, subject to plan rules." },
            { icon: Pill, t: "Pharmacy privileges", d: "Eligible discounts on in-house hospital pharmacy purchases — no third-party paperwork." },
            { icon: Sparkles, t: "Wellness initiatives", d: "Access to health awareness programs, community workshops and webinars across the year." },
            { icon: ShieldCheck, t: "Long-term care relationship", d: "Continuous healthcare support under one program — built for lifetime patient relationships, not one-off transactions." },
          ].map((b, i) => (
            <div key={i} className="reveal lux-card rounded-2xl p-7" style={{ animationDelay: `${i * 90}ms` }}>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1E4F87] to-[#0F2540] text-white grid place-items-center shadow-lg">
                <b.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-[#0F2540]">{b.t}</h3>
              <p className="mt-2 text-sm text-[#5C6B7A] leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section> */}

      {/* SMART HEALTHCARE PRIVILEGE PROGRAM — wide refined dark panel */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-8 lg:py-10" data-testid="home-smart-program-section">
        <div className="max-w-3xl reveal">
          <span className="overline">The privilege difference</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#0F2540] leading-tight">
            Smart healthcare privilege program for <span className="text-[#A53A2A]">your family.</span>
          </h2>
        </div>

        {/* Refined wide dark panel */}
        <div className="mt-6 lg:mt-7 reveal" style={{ animationDelay: "150ms" }}>
          <div className="relative rounded-[2rem] bg-gradient-to-br from-[#0A1929] via-[#1B3253] to-[#0A1929] text-white overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,37,64,0.6)]">
            {/* Decorative glows */}
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#C89B3C]/18 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-24 w-[460px] h-[460px] rounded-full bg-[#A53A2A]/15 blur-3xl pointer-events-none" />
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[length:22px_22px] pointer-events-none" />

            <div className="relative grid lg:grid-cols-12 gap-7 lg:gap-10 p-6 sm:p-8 lg:p-9">
              {/* Left: title + Privilege Card visual */}
              <div className="lg:col-span-7 flex flex-col">
                <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-white/8 border border-[#C89B3C]/30 backdrop-blur-sm w-fit">
                  <Sparkles className="h-3.5 w-3.5 text-[#C89B3C]" />
                  <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#F5C6BE]">Care par excellence</span>
                </div>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-[1.05] tracking-tight">
                  Simple. Affordable.<br />
                  <span className="bg-gradient-to-r from-[#F5E2B1] via-[#E3BC58] to-[#9C7421] bg-clip-text text-transparent">Valuable.</span>
                </h3>
                <p className="mt-3 text-sm lg:text-base text-white/75 max-w-xl leading-relaxed">
                  One smart program that ties together every healthcare moment your family will face this year — built on compassion, trust, and timely care.
                </p>

                {/* Privilege Card mock — proper ID-1 credit-card aspect ratio */}
                <div className="mt-5 lg:mt-6 max-w-[22rem] w-full">
                  <div className="relative w-full aspect-[1.586/1] rounded-2xl bg-gradient-to-br from-[#1B3253] via-[#244A7C] to-[#0F2540] ring-1 ring-[#C89B3C]/25 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.6)] overflow-hidden">
                    {/* Decorative glow */}
                    <div className="absolute -top-14 -right-14 w-44 h-44 rounded-full bg-[#C89B3C]/18 blur-2xl pointer-events-none" />
                    {/* Subtle stripe */}
                    <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-[#E3BC58] via-[#C89B3C] to-[#9C7421] opacity-80" />

                    {/* Top row: brand + tier */}
                    <div className="absolute inset-x-0 top-0 flex items-start justify-between p-4">
                      <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.28em] text-[#F5C6BE] font-semibold">Nirmal Privilege Card</div>
                      <div className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#E3BC58] to-[#9C7421] text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-[#0F2540] shadow-lg whitespace-nowrap">Gold · Family</div>
                    </div>

                    {/* Center wordmark */}
                    <div className="absolute inset-0 flex flex-col items-start justify-center pl-4">
                      <div className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">PRIVILEGE</div>
                      <div className="text-[10px] sm:text-[11px] text-white/65 italic mt-0.5">Care par excellence</div>
                    </div>

                    {/* Bottom row: member id + validity */}
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4">
                      <div>
                        <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-white/50">Member ID</div>
                        <div className="font-mono text-xs sm:text-sm text-white">NPC XXXX XX</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[8px] sm:text-[9px] uppercase tracking-wider text-white/50">Valid till</div>
                        <div className="font-mono text-[11px] sm:text-xs text-white">12 / 2026</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: stat tiles + CTA + location */}
              <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-9 flex flex-col">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    ["₹2,000", "Starting"],
                    ["12 mo", "Validity"],
                    ["24×7", "Helpline"],
                  ].map(([k, v]) => (
                    <div key={v} className="rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur-sm px-3 py-3 text-center hover:bg-white/[0.08] hover:border-[#C89B3C]/30 transition-all duration-500">
                      <div className="font-display text-lg lg:text-xl font-bold text-white leading-none">{k}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/55 font-semibold mt-1.5">{v}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid gap-2">
                  {[
                    [ShieldCheck, "Transparent pricing — no hidden charges"],
                    [Award,       "NABH-aligned protocols across departments"],
                    [Users,       "25+ specialists across 17 departments"],
                  ].map(([Icon, t]) => (
                    <div key={t} className="flex items-center gap-3 text-sm text-white/85">
                      <span className="h-8 w-8 rounded-lg bg-white/[0.06] border border-white/10 grid place-items-center text-[#C89B3C] flex-shrink-0">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to="/join" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[#0F2540] bg-gradient-to-r from-[#F5C6BE] via-[#E3BC58] to-[#C89B3C] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(200,155,60,0.5)] transition-all duration-300" data-testid="simple-panel-cta-join">
                    Join NPC <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a href="tel:+916357033301" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white border border-white/25 hover:bg-white/10 transition-all duration-300" data-testid="simple-panel-cta-call">
                    <Phone className="h-4 w-4" /> Talk to advisor
                  </a>
                </div>

                <div className="mt-auto pt-4 border-t border-white/10 flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-[#F5C6BE] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="uppercase tracking-[0.22em] text-[10px] font-bold text-[#F5C6BE] mb-0.5">Available exclusively at</div>
                    <div className="text-sm font-bold text-white">Nirmal Super Speciality Hospital</div>
                    <div className="text-xs text-white/55 mt-0.5">Adajan · L. P. Savani · Surat — Gujarat</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <p className="mt-5 text-xs text-[#5C6B7A] italic">*Subject to plan eligibility and program terms.</p> */}
      </section>

      {/* PLANS PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="home-plans-section">
        <div className="flex items-end justify-between flex-wrap gap-4 reveal">
          <div className="max-w-xl">
            <span className="overline">Choose your tier</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">NPC plans for every family.</h2>
          </div>
          <Link to="/plans" className="btn-ghost" data-testid="home-view-all-plans">See all plans <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((p, i) => <PlanCard key={p.plan_id} plan={p} index={i} />)}
        </div>
      </section>

      {/* DOCTORS */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 reveal">
          <div className="max-w-xl">
            <span className="overline">Meet the team</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">30+ doctors. <span className="text-[#A53A2A]">One trusted family.</span></h2>
            <p className="mt-3 text-[#5C6B7A]">Every member of our consulting team has 15+ years of clinical experience across India’s top medical institutions.</p>
          </div>
          <Link to="/about" className="btn-ghost">View all doctors <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {DOCTORS.map((d, i) => (
            <div key={d.n} className="reveal group rounded-2xl overflow-hidden lux-card" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="aspect-[4/5] relative bg-gradient-to-br from-[#EAF1FA] to-white">
                <img src={docAvatar(d)} alt={d.n} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold">
                  <span className="bg-white/20 backdrop-blur-sm border border-white/30 px-2 py-0.5 rounded-full">{d.e}</span>
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-lg font-semibold text-[#0F2540]">{d.n}</div>
                <div className="text-xs uppercase tracking-wider text-[#A53A2A] mt-1">{d.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section> */}

      {/* LIFESTYLE SPLIT */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative reveal">
          <div className="rounded-[2rem] overflow-hidden ring-1 ring-[#E1E8F0]">
            <img src={IMG.family} alt="Family" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-6 lux-card rounded-2xl p-5 w-60 hidden md:block">
            <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">Member savings</div>
            <div className="font-display text-3xl font-bold text-[#1F7A3D] mt-1">₹38,500</div>
            <div className="text-xs text-[#5C6B7A] mt-1">avg. yearly savings on diagnostics + OPD</div>
          </div>
        </div>
        <div className="reveal">
          <span className="overline">Why members love us</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">Care that feels personal. Access that feels unlimited.</h2>
          <p className="mt-4 text-[#5C6B7A] text-lg leading-relaxed">
            Every member gets a dedicated relationship manager, a digital NPC card with QR, and one dashboard for the whole family — built for Adajan, by people from Adajan.
          </p>
          <ul className="mt-6 space-y-3 text-[#0F2540]">
            {["Unique digital ID & QR card", "Family healthcare ecosystem", "In-house pharmacy privileges", "All eligible specialties covered", "Wellness camps across the year"].map((x) => (
              <li key={x} className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#1F7A3D]" /> {x}</li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <Link to="/plans" className="btn-primary">Become a member <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/why-us" className="btn-ghost">Why us</Link>
          </div>
        </div>
      </section> */}

      {/* TESTIMONIALS — fixed visibility, premium carousel */}
      {/* <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20" data-testid="real-stories-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 reveal">
          <div className="max-w-xl">
            <span className="overline">Real member stories</span>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-[#0F2540]">From families. <span className="text-[#A53A2A]">In their own words.</span></h2>
          </div>
          <div className="flex items-center gap-3 text-[#0F2540]">
            <div className="flex">{[...Array(5)].map((_, k) => <Star key={k} className="h-5 w-5 fill-[#C89B3C] text-[#C89B3C]" />)}</div>
            <div className="text-sm"><span className="font-semibold">4.9 / 5</span> · 2,400+ reviews</div>
          </div>
        </div>
        <TestimonialCarousel />
      </section> */}

      {/* CTA STRIP — premium */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="relative rounded-[2.5rem] mesh-dark p-10 lg:p-16 overflow-hidden reveal">
          <div className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#C89B3C]/20 blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <span className="overline text-[#C89B3C]">Limited launch · Adajan</span>
              <h3 className="mt-3 font-display text-4xl lg:text-5xl font-bold leading-tight">Become an NPC member today.</h3>
              <p className="mt-4 text-white/85 text-lg max-w-2xl">From ₹2,000/year (incl. 18% GST). Activate online in under 5 minutes. Digital card with QR is issued instantly. Add your family anytime.</p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#C89B3C]" /> No hidden charges</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#C89B3C]" /> Instant digital card</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#C89B3C]" /> Family coverage</span>
              </div>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
              <Link to="/join" className="bg-white text-[#0F2540] px-7 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-[#C89B3C] hover:text-[#0F2540] transition-all shadow-lg">Join NPC <ArrowRight className="h-4 w-4" /></Link>
              <a href="tel:+916357033301" className="border border-white/40 text-white px-7 py-4 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-white/10 transition"><Phone className="h-4 w-4" /> +91-63570-33301</a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

/* ────────── HERO SLIDER ────────── */
function HeroSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((p) => (p + 1) % HERO_SLIDES.length);
    }, 4000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#E1E8F0] shadow-2xl tilt h-[460px]">
      {HERO_SLIDES.map((slide, i) => (
        <img
          key={i}
          src={slide.src}
          alt={slide.alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            active === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              active === i
                ? "w-8 bg-white"
                : "w-2.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
    </div>
  );
}